/**
 * monacoTSWorkspace — feeds the project's TypeScript / JavaScript files into
 * Monaco's built-in TypeScript worker so cross-file completions, hover info,
 * error markers, and go-to-definition work the way they do in WebStorm or
 * VS Code.
 *
 * Strategy is hybrid: eager-load up to EAGER_LIMIT files on project mount
 * (typically the whole project for normal-sized codebases) and lazy-load any
 * imports the user happens to open beyond that. Watcher events keep the
 * loaded set in sync with what's actually on disk.
 *
 * If a `tsconfig.json` exists at the project root we read its `compilerOptions`
 * and apply them to the worker so things like `paths`, `baseUrl`, `jsx`, and
 * `target` match what the project actually expects.
 */
import type { Monaco } from '@monaco-editor/react';
import type { IBackend } from '@/services/backend/IBackend';
import type { FileIndexEntry } from '@/services/backend/types';
import { logger } from '@/lib/logger';
import { CacheKeys, envelope, readCache, writeCache, type CacheEnvelope } from '@/lib/projectCache';
import { useCacheStatusStore } from '@/stores/cacheStatusStore';

const TAG = 'TSWorkspace';
const TS_EXT_RE = /\.(tsx?|jsx?|mts|cts)$/i;
/**
 * Hard-coded "never eager-load these" path segments. We can't trust
 * indexProjectFiles' .gitignore filtering alone — projects with sparse or
 * pnpm-style gitignores let node_modules paths sneak through, and a pnpm
 * node_modules can hold tens of thousands of .cjs.js files. Eager-loading
 * those saturates the backend and starves user file clicks behind it.
 */
const ALWAYS_EXCLUDE_SEGMENTS = [
  'node_modules',
  '.git',
  'target',
  'dist',
  'build',
  '.next',
  '.nuxt',
  '.svelte-kit',
  '.cache',
  'out',
  '.turbo',
];
const EXCLUDE_RE = new RegExp(
  `(^|/)(?:${ALWAYS_EXCLUDE_SEGMENTS.map((s) => s.replace(/\./g, '\\.')).join('|')})(/|$)`,
);
/**
 * Smaller cap than before — Monaco's TS worker handles ~500 files comfortably,
 * 2000 was unnecessary and made cold start IO-heavy. Most projects fit under
 * the cap; the rest lazy-load on file open.
 */
const EAGER_LIMIT = 500;
const TS_CACHE_VERSION = 2; // bumped: previous cache may include node_modules junk

interface CachedFile {
  /** Project-relative path with forward slashes. */
  path: string;
  /** File content fed to Monaco's TS worker. */
  content: string;
}

interface TsWorkspaceCachePayload {
  files: CachedFile[];
}

class TSWorkspace {
  private monaco: Monaco | null = null;
  private projectId: string | null = null;
  private projectRoot: string | null = null;
  private backend: IBackend | null = null;
  /** Per-file Monaco IDisposable (so we can dispose on remove/refresh). */
  private loaded = new Map<string, { dispose: () => void }>();
  /** Per-file content kept so we can serialize to the cache. */
  private contents = new Map<string, string>();

  /**
   * Initialize for a project. Cheap no-op when already initialized for the
   * same project (e.g. handleEditorMount fires every time the editor
   * remounts — without this guard each click would refire the full eager
   * load and saturate the backend).
   */
  async init(monaco: Monaco, projectId: string, projectRoot: string, backend: IBackend): Promise<void> {
    if (this.projectId === projectId && this.projectRoot === projectRoot && this.monaco === monaco) {
      return;
    }
    // Wrap the whole init in a top-level try/catch so any unexpected error
    // (Monaco API mismatch, JSON.parse on a malformed tsconfig, etc.) gets
    // surfaced in the logs instead of silently breaking the editor.
    try {
      this.disposeAll();
      this.monaco = monaco;
      this.projectId = projectId;
      this.projectRoot = projectRoot;
      this.backend = backend;

      this.applyDefaultCompilerOptions();
      await this.applyProjectTsconfig();

      // 1. Try cache for instant intellisense. addLib for every cached file
      //    feeds Monaco's TS worker without doing any disk IO.
      const hadCache = await this.loadFromCache();

      // 2. Validate against current project state. If we had no cache, this
      //    is the first eager load. If we did, this catches new files since
      //    last session and removes deleted ones.
      useCacheStatusStore.getState().begin('ts-workspace');
      try {
        await this.validateAndRefresh(hadCache);
        // Persist the post-refresh state for next session.
        await this.writeCache();
      } finally {
        useCacheStatusStore.getState().end('ts-workspace');
      }
    } catch (e) {
      logger.warn(TAG, 'init failed; intellisense may be limited:', e);
      // Don't rethrow — Monaco still works as a plain editor without us.
    }
  }

  /** Tear down all libs and detach. Call on project switch / unmount. */
  dispose(): void {
    this.disposeAll();
    this.monaco = null;
    this.projectId = null;
    this.projectRoot = null;
    this.backend = null;
  }

  /** Apply a watcher event. Re-read created/modified files; remove deleted. */
  async onFileEvent(relPath: string, changeType: 'created' | 'modified' | 'deleted'): Promise<void> {
    if (!this.monaco || !this.backend || !this.projectId) return;
    if (!TS_EXT_RE.test(relPath)) return;

    if (changeType === 'deleted') {
      this.removeFile(relPath);
      return;
    }

    // For modified/created, only refresh if the file is already loaded.
    // Newly created files outside the loaded set will get picked up if/when
    // the user opens them via ensureFileImports().
    if (this.loaded.has(relPath)) {
      try {
        const content = await this.backend.files.readFile(this.projectId, this.absolutize(relPath));
        this.addLib(relPath, content);
      } catch (e) {
        logger.debug(TAG, `Failed to refresh ${relPath}:`, e);
      }
    }
  }

  /**
   * Lazy-load: when the user opens a TS/JS file, scan its import statements
   * and pre-load any project files it directly references. Cheap regex parse
   * — good enough for the editor experience without spinning up a real
   * resolver.
   */
  async ensureFileImports(relPath: string, content: string): Promise<void> {
    if (!this.monaco || !this.backend || !this.projectId) return;
    if (!TS_EXT_RE.test(relPath)) return;

    const imports = this.parseRelativeImports(content);
    if (imports.length === 0) return;

    const resolved: string[] = [];
    for (const spec of imports) {
      const target = this.resolveImportTo(relPath, spec);
      if (target && !this.loaded.has(target)) {
        resolved.push(target);
      }
    }
    if (resolved.length === 0) return;

    await this.loadFiles(resolved);
  }

  // ─── Internal ────────────────────────────────────────────────────────────

  private applyDefaultCompilerOptions(): void {
    if (!this.monaco) return;
    const ts = this.monaco.languages.typescript;
    const opts = {
      target: ts.ScriptTarget.ESNext,
      module: ts.ModuleKind.ESNext,
      moduleResolution: ts.ModuleResolutionKind.NodeJs,
      jsx: ts.JsxEmit.Preserve,
      allowJs: true,
      checkJs: false,
      esModuleInterop: true,
      allowSyntheticDefaultImports: true,
      resolveJsonModule: true,
      // Less strict in the editor than at build time — fewer red squiggles
      // for things the editor can't fully see (e.g. unresolved aliases).
      strict: false,
      noEmit: true,
      skipLibCheck: true,
    };
    ts.typescriptDefaults.setCompilerOptions(opts);
    ts.javascriptDefaults.setCompilerOptions(opts);
    // Suppress noisy diagnostics for missing imports — we're a partial view
    // of the project, not the full build.
    ts.typescriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false,
      noSuggestionDiagnostics: false,
    });
  }

  private async applyProjectTsconfig(): Promise<void> {
    if (!this.monaco || !this.backend || !this.projectId) return;
    try {
      const raw = await this.backend.files.readFile(
        this.projectId,
        this.absolutize('tsconfig.json'),
      );
      const parsed = JSON.parse(stripJsonComments(raw)) as {
        compilerOptions?: Record<string, unknown>;
      };
      if (!parsed.compilerOptions) return;

      const ts = this.monaco.languages.typescript;
      const current = ts.typescriptDefaults.getCompilerOptions();
      // Hand-mapping numeric enums so users can write the string form ("ESNext")
      // in their tsconfig and we still translate it.
      const mapped = {
        ...current,
        ...mapTsconfigOptions(parsed.compilerOptions, ts),
      };
      ts.typescriptDefaults.setCompilerOptions(mapped);
      ts.javascriptDefaults.setCompilerOptions(mapped);
      logger.info(TAG, 'Applied project tsconfig.json compilerOptions');
    } catch (e) {
      logger.debug(TAG, 'No tsconfig.json or failed to parse:', e);
    }
  }

  /**
   * Read the per-project TS workspace cache and addLib every entry. Returns
   * true if the cache was applied (so the validate pass knows it's only
   * looking for deltas, not doing a full eager load).
   */
  private async loadFromCache(): Promise<boolean> {
    if (!this.projectId) return false;
    const cached = await readCache<CacheEnvelope<TsWorkspaceCachePayload>>(
      this.projectId,
      CacheKeys.tsWorkspace,
    );
    if (!cached || cached.schemaVersion !== TS_CACHE_VERSION) return false;
    const t0 = performance.now();
    for (const f of cached.payload.files) {
      this.addLib(f.path, f.content);
    }
    logger.info(
      'FileTreePerf',
      `TS workspace from cache: ${cached.payload.files.length} files in ${(performance.now() - t0).toFixed(0)}ms (cached ${new Date(cached.cachedAt).toISOString()})`,
    );
    return true;
  }

  /**
   * Diff the loaded set against the current project files: load anything
   * new (capped at EAGER_LIMIT total), drop anything deleted while we were
   * closed. On a cold first run this is the original eager-load.
   */
  private async validateAndRefresh(hadCache: boolean): Promise<void> {
    if (!this.backend || !this.projectId || !this.projectRoot) return;
    const t0 = performance.now();
    let entries: FileIndexEntry[] = [];
    try {
      entries = await this.backend.files.indexProjectFiles(this.projectId);
    } catch (e) {
      logger.warn(TAG, 'indexProjectFiles failed; refresh skipped', e);
      return;
    }
    // indexProjectFiles returns ABSOLUTE filesystem paths. Strip the project
    // root prefix so we can keep keying the loaded map by project-relative
    // path (and so absolutize() — which joins root + relative — produces the
    // right thing instead of e.g. C:\proj\C:\proj\foo.ts).
    //
    // Also enforce the ALWAYS_EXCLUDE filter — even if the project's gitignore
    // failed to cover node_modules/etc, we never feed those into the worker.
    const tsPaths = new Set(
      entries
        .filter((e) => e.type === 'file' && TS_EXT_RE.test(e.path))
        .map((e) => this.relativize(e.path))
        .filter((p): p is string => p !== null && !EXCLUDE_RE.test(p)),
    );

    // Drop files that were in the cache but no longer exist on disk.
    let removed = 0;
    for (const loadedPath of Array.from(this.loaded.keys())) {
      if (!tsPaths.has(loadedPath)) {
        this.removeFile(loadedPath);
        removed += 1;
      }
    }

    // Load new files we haven't seen, up to the remaining capacity.
    const remaining = Math.max(0, EAGER_LIMIT - this.loaded.size);
    const notLoaded = Array.from(tsPaths)
      .filter((p) => !this.loaded.has(p))
      .sort((a, b) => depthOf(a) - depthOf(b));
    const slice = notLoaded.slice(0, remaining);
    if (slice.length > 0) {
      await this.loadFiles(slice);
    }
    if (notLoaded.length > slice.length) {
      logger.warn(
        TAG,
        `${notLoaded.length - slice.length} TS files not loaded (cap ${EAGER_LIMIT}). They lazy-load on open.`,
      );
    }

    logger.info(
      'FileTreePerf',
      `TS workspace ${hadCache ? 'validate' : 'eager load'}: ${(performance.now() - t0).toFixed(0)}ms (${this.loaded.size}/${tsPaths.size} loaded; +${slice.length}, -${removed})`,
    );
  }

  /** Persist current loaded set to disk for next session's instant load. */
  private async writeCache(): Promise<void> {
    if (!this.projectId) return;
    const files: CachedFile[] = [];
    for (const [path, content] of this.contents) {
      files.push({ path, content });
    }
    await writeCache(
      this.projectId,
      CacheKeys.tsWorkspace,
      envelope<TsWorkspaceCachePayload>({ files }, TS_CACHE_VERSION),
    );
  }

  private async loadFiles(paths: string[]): Promise<void> {
    if (!this.backend || !this.projectId) return;
    // Parallel fetch with a small concurrency cap to avoid hammering the
    // backend / IO with thousands of in-flight reads.
    const CONCURRENCY = 32;
    let i = 0;
    const workers = Array.from({ length: CONCURRENCY }, async () => {
      while (i < paths.length) {
        const idx = i++;
        const rel = paths[idx];
        try {
          const content = await this.backend!.files.readFile(this.projectId!, this.absolutize(rel));
          this.addLib(rel, content);
        } catch {
          // Quietly skip files we can't read (binary marked as text, perm errors, etc.)
        }
      }
    });
    await Promise.all(workers);
  }

  private addLib(relPath: string, content: string): void {
    if (!this.monaco) return;
    // Dispose any prior version so updates don't accumulate stale libs.
    this.loaded.get(relPath)?.dispose();
    const ts = this.monaco.languages.typescript;
    // Use a private URI scheme so our workspace libs can't collide with the
    // editor's own model URIs (which are file:/// based). A collision was
    // causing Monaco to render blank when it found a same-URI lib.
    const uri = `ts:project/${relPath.replace(/^\/+/, '')}`;
    const disp = ts.typescriptDefaults.addExtraLib(content, uri);
    this.loaded.set(relPath, disp);
    this.contents.set(relPath, content);
  }

  private removeFile(relPath: string): void {
    const disp = this.loaded.get(relPath);
    if (disp) {
      disp.dispose();
      this.loaded.delete(relPath);
      this.contents.delete(relPath);
    }
  }

  private disposeAll(): void {
    for (const d of this.loaded.values()) d.dispose();
    this.loaded.clear();
    this.contents.clear();
  }

  private absolutize(relPath: string): string {
    if (!this.projectRoot) return relPath;
    const root = this.projectRoot;
    const sep = root.includes('\\') && !root.includes('/') ? '\\' : '/';
    const norm = sep === '\\' ? relPath.replace(/\//g, '\\') : relPath;
    if (root.endsWith('/') || root.endsWith('\\')) return root + norm;
    return root + sep + norm;
  }

  /**
   * Convert an absolute filesystem path to a project-relative one with
   * forward slashes. Returns null if the path doesn't live under the project
   * root (and thus can't be made relative). Case-insensitive prefix compare
   * so Windows mixed-case drives don't fool us.
   */
  private relativize(absPath: string): string | null {
    if (!this.projectRoot) return null;
    const normRoot = this.projectRoot.replace(/\\/g, '/').replace(/\/$/, '');
    const normAbs = absPath.replace(/\\/g, '/');
    if (normAbs.toLowerCase().startsWith(normRoot.toLowerCase() + '/')) {
      return normAbs.slice(normRoot.length + 1);
    }
    // Already relative? Common when callers feed us a project-relative id.
    if (!normAbs.includes(':') && !normAbs.startsWith('/')) {
      return normAbs;
    }
    return null;
  }

  private parseRelativeImports(content: string): string[] {
    // Match: import ... from '...';  /  import('...')  /  require('...')
    // Only relative paths ('./' or '../') — bare specifiers are npm packages
    // we can't resolve from disk anyway.
    const out: string[] = [];
    const re = /(?:from\s+|import\s*\(|require\s*\(\s*)['"](\.{1,2}\/[^'"]+)['"]/g;
    let m;
    while ((m = re.exec(content)) !== null) {
      out.push(m[1]);
    }
    return out;
  }

  private resolveImportTo(fromRel: string, spec: string): string | null {
    const fromDir = fromRel.includes('/') ? fromRel.substring(0, fromRel.lastIndexOf('/')) : '';
    const segments = (fromDir + '/' + spec).split('/').filter(Boolean);
    const stack: string[] = [];
    for (const seg of segments) {
      if (seg === '.') continue;
      if (seg === '..') stack.pop();
      else stack.push(seg);
    }
    const base = stack.join('/');
    // The spec might omit the extension. Try common ones.
    const candidates = [
      base,
      `${base}.ts`,
      `${base}.tsx`,
      `${base}.js`,
      `${base}.jsx`,
      `${base}/index.ts`,
      `${base}/index.tsx`,
      `${base}/index.js`,
      `${base}/index.jsx`,
    ];
    for (const c of candidates) {
      if (this.loaded.has(c)) return null; // already loaded
      // We can't sync-check existence here; speculatively return the most
      // likely match. The fetch will silently fail for non-existent paths.
    }
    // Prefer .ts → .tsx → .js variants in that order.
    return `${base}.ts`;
  }
}

function depthOf(path: string): number {
  return path.replace(/\\/g, '/').split('/').filter(Boolean).length;
}

function stripJsonComments(text: string): string {
  // Basic JSONC support: strip // line comments and /* */ block comments.
  // Doesn't handle strings containing comment-like sequences perfectly, but
  // tsconfig.json files in the wild rarely do.
  return text
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/^\s*\/\/.*$/gm, '');
}

function mapTsconfigOptions(
  raw: Record<string, unknown>,
  ts: Monaco['languages']['typescript'],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    switch (key) {
      case 'target':
      case 'module':
      case 'moduleResolution':
      case 'jsx': {
        const enumName = key === 'target' ? 'ScriptTarget'
          : key === 'module' ? 'ModuleKind'
          : key === 'moduleResolution' ? 'ModuleResolutionKind'
          : 'JsxEmit';
        const enumObj = (ts as Record<string, Record<string, unknown>>)[enumName];
        if (typeof value === 'string' && enumObj) {
          // tsconfig values are case-insensitive; Monaco enums use PascalCase.
          const matched = Object.keys(enumObj).find(
            (k) => k.toLowerCase() === String(value).toLowerCase(),
          );
          if (matched) out[key] = enumObj[matched];
        }
        break;
      }
      case 'paths':
      case 'baseUrl':
      case 'allowJs':
      case 'checkJs':
      case 'strict':
      case 'esModuleInterop':
      case 'allowSyntheticDefaultImports':
      case 'resolveJsonModule':
      case 'jsxFactory':
      case 'jsxFragmentFactory':
      case 'jsxImportSource':
        out[key] = value;
        break;
      default:
        // Pass through anything else verbatim — Monaco may know fields we don't.
        out[key] = value;
    }
  }
  return out;
}

export const tsWorkspace = new TSWorkspace();
